<template>
    <div>

        <FullCalendar
            ref="AllReservationsCalendar"
            :options="config"/>

        <ReservationModal
            :slotId="updateReservationModal"
            @close="updateReservationModal = null"/>

    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import ReservationModal from '@/ReservationModal/Index';
import { debounce, throttle } from 'lodash';

export default {
    components: {
        FullCalendar,
        ReservationModal,
    },

    props: {
        view: String,
        scrollToDate: String,
        addQuery: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            calendarApi: null,
            ownReservations: [],
            otherReservations: [],
            updateReservationModal: null,
            dateRangeFilter: null,
        }
    },

    watch: {
        dateRangeFilter(val) {
            this.throttleFetchReservations();
        }
    },

    computed: {
        initialView() {
            return this.view;
        },
        initialDate() {
            return this.scrollToDate;
        },
        config() {
            return {
                ...this.configOptions,
                ...this.eventHandlers,
            }
        },
        configOptions() {
            return {
                plugins: [interactionPlugin, timeGridPlugin, dayGridPlugin],
                initialView: this.initialView,
                initialDate: this.initialDate,
                firstDay: 1,
                locale: 'cs',
                allDaySlot: false,
                height: '100%',
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'timeGridDay,timeGridWeek,dayGridMonth'
                },
                buttonText: {
                    today: 'dnes',
                    day: 'den',
                    week: 'týden',
                    month: 'měsíc',
                },
                eventColor: 'var(--accent)',
                eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    omitZeroMinute: false
                },

                selectable: true,
                selectConstraint: 'businessHours',

                eventSources: [
                    {
                        events: this.otherReservations,
                    },
                    {
                        events: this.ownReservations,
                        color: 'var(--pef)'
                    }
                ],
                businessHours: this.openSlots,
            }
        },
        eventHandlers() {
            return {
                datesSet: this.calendarDateRangeChanged,

                eventClick: (info) => {
                    this.openReservationModal(info.event.id);
                },

                eventDidMount: (info) => {
                    info.el.setAttribute('data-id', info.event.id);
                }
            }
        }
    },

    methods: {
        refreshDateRangeFilter(info) {
            this.dateRangeFilter = info.start.addDays(-32).toYmdString()
                + ','
                + info.end.addDays(32).toYmdString()
        },

        calendarDateRangeChanged(info) {
            this.refreshDateRangeFilter(info);

            if (window.App.auth) {
                this.debounceFlashDateRangeToSession(info.startStr);
            }
        },

        async fetchReservations() {
            const endpoint = window.Uri()
                .setPath('/api/v1/reservations')
                .addQuery('filter[date_between]', this.dateRangeFilter)
                .addQuery('include', 'resource')
                .addQuery('append', 'title')
                .addQuery('json', 'true');

            Object.keys(this.addQuery)
                .forEach((key, index) => endpoint.addQuery(key, this.addQuery[key]));

            // guest has no own reservation
            if (! window.App.auth) {
                this.otherReservations = await axios
                    .get(endpoint.toString())
                    .then(response => response.data.reservations);
                return;
            }

            // owner filter is inherited from request
            const filterOwnerId = window.Uri().getQuery('filter[owner_id]');

            if (filterOwnerId) {
                let filteredReservations = await axios
                    .get(endpoint.toString())
                    .then(response => response.data.reservations);

                filterOwnerId == window.App.auth.id
                    ? this.ownReservations = filteredReservations
                    : this.otherReservations = filteredReservations;

                return;
            }

            // default view, separated own and other reservations
            this.ownReservations = await axios
                .get(endpoint
                    .addQuery('filter[owner_id]', window.App.auth.id)
                    .toString()
                )
                .then(response => response.data.reservations);

            this.otherReservations = await axios
                .get(endpoint
                    .addQuery('filter[owner_id]', window.App.auth.id * -1)
                    .toString()
                )
                .then(response => response.data.reservations);
        },

        throttleFetchReservations: throttle(function () {
            this.fetchReservations();
        }, 1000),

        async flashDateRangeToSession(firstDay) {
            let response = await axios.patch('/reservations/flash-date-range-to-session', {
                firstDay,
                view: this.calendarApi?.view.type
            });
        },

        debounceFlashDateRangeToSession: debounce(function (firstDay) {
            this.flashDateRangeToSession(firstDay);
        }, 1000),

        openReservationModal(slotId) {
            this.updateReservationModal = slotId;
        },
    },

    mounted() {
        this.calendarApi = this.$refs.AllReservationsCalendar.getApi();

        window.events.$on('refreshCalendar', () => this.fetchReservations());
    }
};
</script>
